import { StorageError } from '@wiztime/storage';
import Dexie from 'dexie';
import { BASE_BACKOFF_MS, MAX_BACKOFF_MS } from './constants';
import { ServerErrorCode, SyncHttpError, SyncNetworkError, SyncProtocolError } from './errors';
import { SyncErrorCode, SyncState } from './status';

export interface FailureClass {
  readonly state: SyncState;
  readonly code: SyncErrorCode;
  /** Authentication failed: automatic sync stops until the user retries or re-enables sync. */
  readonly pause: boolean;
  /** Seconds the server asked to wait; null when the client picks the delay. */
  readonly retryAfter: number | null;
}

/** Map an exception of a sync run to the status it produces. */
export function classifyFailure(error: unknown): FailureClass {
  if (error instanceof SyncNetworkError) {
    return { state: SyncState.Offline, code: SyncErrorCode.Network, pause: false, retryAfter: null };
  }
  if (error instanceof SyncHttpError) {
    const retry = (code: SyncErrorCode): FailureClass => ({
      state: SyncState.Error,
      code,
      pause: false,
      retryAfter: error.retryAfter,
    });
    switch (error.status) {
      case 404:
        return {
          state: SyncState.SpaceMissing,
          code: SyncErrorCode.SpaceMissing,
          pause: true,
          retryAfter: null,
        };
      case 401:
        return { state: SyncState.Error, code: SyncErrorCode.Unauthorized, pause: true, retryAfter: null };
      case 403:
        if (error.code === ServerErrorCode.PassphraseSpacesDisabled) {
          return {
            state: SyncState.SpaceMissing,
            code: SyncErrorCode.PassphraseSpacesDisabled,
            pause: true,
            retryAfter: null,
          };
        }
        if (error.code === ServerErrorCode.EmailUnverified) return retry(SyncErrorCode.EmailUnverified);
        return { state: SyncState.Error, code: SyncErrorCode.Forbidden, pause: true, retryAfter: null };
      case 429:
        return retry(SyncErrorCode.RateLimited);
      case 503:
        return retry(SyncErrorCode.ServiceUnavailable);
      case 413:
        return retry(
          error.code === ServerErrorCode.QuotaExceeded
            ? SyncErrorCode.QuotaExceeded
            : SyncErrorCode.PayloadTooLarge,
        );
      case 400:
        return retry(SyncErrorCode.InvalidRequest);
      default:
        return retry(SyncErrorCode.ServerError);
    }
  }
  if (error instanceof SyncProtocolError) {
    return { state: SyncState.Error, code: SyncErrorCode.Protocol, pause: false, retryAfter: null };
  }
  const isStorage =
    error instanceof StorageError || error instanceof Dexie.DexieError || error instanceof DOMException;
  return {
    state: SyncState.Error,
    code: isStorage ? SyncErrorCode.Storage : SyncErrorCode.Unexpected,
    pause: false,
    retryAfter: null,
  };
}

/**
 * Delay before the next automatic attempt after the `failures`-th consecutive failure: the server's
 * `Retry-After` when given, otherwise 2 s doubling up to 5 minutes with ±20 % jitter.
 */
export function backoffDelayMs(
  failures: number,
  retryAfterSeconds: number | null,
  random: () => number,
): number {
  if (retryAfterSeconds !== null) return Math.min(MAX_BACKOFF_MS, Math.max(1_000, retryAfterSeconds * 1_000));
  const base = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.max(0, failures - 1));
  return Math.min(MAX_BACKOFF_MS, Math.round(base * (0.8 + 0.4 * random())));
}
